/**
 * QuickBooks Transaction Preview Component
 *
 * Displays a preview of parsed transactions grouped by payment type.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  AlertTitle,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { ParsedTransactions } from '../../../types/quickbooks';

interface TransactionPreviewProps {
  parsedData: ParsedTransactions;
  selectedPaymentTypes: string[];
  duplicateResults?: any;
}

const TransactionPreview: React.FC<TransactionPreviewProps> = ({
  parsedData,
  selectedPaymentTypes,
  duplicateResults,
}) => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  const handlePanelToggle = (panel: string) => {
    setExpandedPanels(prev =>
      prev.includes(panel) ? prev.filter(p => p !== panel) : [...prev, panel]
    );
  };

  // Helper to check if transaction is a duplicate
  const isDuplicate = (transaction: any) => {
    if (!duplicateResults?.duplicates) return false;
    return duplicateResults.duplicates.some(
      (dup: any) =>
        dup.transaction.parent_email === transaction.parent_email &&
        dup.transaction.date === transaction.date &&
        dup.transaction.amount === transaction.amount &&
        dup.transaction.code === transaction.code
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Transaction Preview
      </Typography>

      {/* Duplicate Warning Alert */}
      {duplicateResults?.summary?.duplicates_found > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            ⚠️ {duplicateResults.summary.duplicates_found} Potential Duplicate
            {duplicateResults.summary.duplicates_found !== 1 ? 's' : ''} Found
          </AlertTitle>
          <Typography variant="body2">
            These transactions match previously uploaded receipts from the last
            90 days. Duplicate transactions are highlighted below.
          </Typography>
        </Alert>
      )}

      {selectedPaymentTypes.map(paymentType => {
        const transactions = parsedData.grouped_transactions[paymentType] || [];
        const summary = parsedData.summary[paymentType];
        const isExpanded = expandedPanels.includes(paymentType);

        if (transactions.length === 0) return null;

        return (
          <Accordion
            key={paymentType}
            expanded={isExpanded}
            onChange={() => handlePanelToggle(paymentType)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                pr={2}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6">{paymentType}</Typography>
                  <Chip
                    label={`${summary.count} transaction${summary.count !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                  />
                </Box>
                <Typography variant="h6" color="primary">
                  ${summary.total.toFixed(2)}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Parent Email</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Memo</TableCell>
                      {paymentType === 'Check' && (
                        <TableCell>Check #</TableCell>
                      )}
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction, index) => {
                      const isTransactionDuplicate = isDuplicate(transaction);
                      // Create unique key from multiple transaction fields
                      // Including memo and check_number significantly reduces collision probability
                      const uniqueKey = `${paymentType}-${transaction.student}-${transaction.parent_email}-${transaction.date}-${transaction.amount}-${transaction.code}-${transaction.memo || ''}-${transaction.check_number || ''}-${index}`;

                      return (
                        <TableRow
                          // eslint-disable-next-line react/no-array-index-key
                          key={uniqueKey}
                          sx={{
                            bgcolor: isTransactionDuplicate
                              ? 'warning.light'
                              : 'inherit',
                            opacity: isTransactionDuplicate ? 0.6 : 1,
                          }}
                        >
                          <TableCell>
                            {isTransactionDuplicate && (
                              <Tooltip title="Previously uploaded - duplicate detected">
                                <Chip
                                  icon={<WarningIcon />}
                                  label="Duplicate"
                                  color="warning"
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                              </Tooltip>
                            )}
                            {transaction.student}
                          </TableCell>
                          <TableCell>{transaction.parent_email}</TableCell>
                          <TableCell>
                            {new Date(transaction.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{transaction.code}</TableCell>
                          <TableCell>{transaction.memo}</TableCell>
                          {paymentType === 'Check' && (
                            <TableCell>
                              {transaction.check_number || '-'}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            ${transaction.amount.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {selectedPaymentTypes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Select at least one payment type to preview transactions.
        </Typography>
      )}
    </Box>
  );
};

export default TransactionPreview;
